import { useMemo } from "react";
import { useGlobalSettings } from "./useGlobalSettings";

const readDisplayFlag = (key: string): boolean => {
  const saved = localStorage.getItem(key);
  return saved !== null ? (JSON.parse(saved) as boolean) : false;
};

export interface RoadsAndSewersMarkupFactorOptions {
  includeOhpDisplay?: boolean;
  includePreliminariesDisplay?: boolean;
}

/**
 * Plain (non-hook) formula, for non-component call sites that can't use the
 * hook below — currently `roadsAndSewersBoqSummaryComputer.ts`, which
 * persists this same markup factor into the saved BOQ so it never disagrees
 * with what the hook computes live.
 */
export const computeRoadsAndSewersMarkupFactor = (
  overheadAndProfit: number | undefined,
  preliminaries: number | undefined,
  includeOhpDisplay: boolean,
  includePreliminariesDisplay: boolean,
): number => {
  const ohpPercent = includeOhpDisplay ? overheadAndProfit ?? 0 : 0;
  const prelimsPct = includePreliminariesDisplay ? preliminaries ?? 0 : 0;
  return (1 + ohpPercent / 100) * (1 + prelimsPct / 100);
};

/**
 * The single OH&P/Preliminaries markup factor every Roads & Sewers Bill
 * Summary tab, Sum Analysis total, persisted BOQ summary section, and Run
 * Matrix cell must share — `(1 + OH&P%) * (1 + Preliminaries%)`, gated by
 * the same `roadsAndSewers_includeOhpDisplay` / `roadsAndSewers_includePreliminariesDisplay`
 * localStorage flags the checkboxes on ReviewCalculate.tsx's "Bill of
 * Quantities Summary" tab bar set (mirrored into RunMatrix.tsx's own
 * read-only Preliminaries-only checkboxes). Pass explicit overrides only
 * when a caller already has the live checkbox state in hand (e.g.
 * ReviewCalculate.tsx itself); every other caller should call with no
 * options so it reads the flags fresh, the same way `useRoadsAndSewersTotalSync`
 * does.
 */
export const useRoadsAndSewersMarkupFactor = (
  options?: RoadsAndSewersMarkupFactorOptions,
): number => {
  const { settings } = useGlobalSettings();
  const includeOhpDisplay =
    options?.includeOhpDisplay ?? readDisplayFlag("roadsAndSewers_includeOhpDisplay");
  const includePreliminariesDisplay =
    options?.includePreliminariesDisplay ?? readDisplayFlag("roadsAndSewers_includePreliminariesDisplay");

  return useMemo(
    () => computeRoadsAndSewersMarkupFactor(
      settings.roadsAndSewers.overheadAndProfit,
      settings.roadsAndSewers.preliminaries,
      includeOhpDisplay,
      includePreliminariesDisplay,
    ),
    [
      settings.roadsAndSewers.overheadAndProfit,
      settings.roadsAndSewers.preliminaries,
      includeOhpDisplay,
      includePreliminariesDisplay,
    ],
  );
};
